import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search as FiSearch } from 'lucide-react';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import LoadingState from '../components/LoadingState';
import Pagination from '../components/ui/Pagination';
import { listPaymentVouchers } from '../lib/accountsPayablePaymentsApi';
import type { PaymentVoucher, PaymentVoucherList } from '../lib/accountsPayablePaymentsApi';
import { formatDate as fmtDate, storedDay } from "../utils/dateFormat";
import { downloadObjectsCsv } from '../utils/downloadCsv';
// A bill's money: TT$ / US$ from the label owner, never the locale's bare "$" (lane 5, V3d). The cards
// total each currency on its own (CurrencyLines): payments in two currencies are never one figure.
import { formatBillMoney as formatMoney } from '../features/accountsPayableReports/format';
import { CurrencyLines } from '../features/accountsPayableReports/CurrencyLines';

// A payment date is a stored CALENDAR DAY (UTC midnight, D109.13): print its own day.
function formatDate(value?: string | null) {
  return fmtDate(storedDay(value), '--');
}

const INFO = (
  <div className="space-y-2">
    <p>This screen tracks all payment vouchers created for Accounts Payable.</p>
    <p>You can filter vouchers by supplier, payment method, and search by reference.</p>
    <p>
      A voided payment stays here, marked Void, and is left out of the totals. &quot;Unallocated only&quot; lists
      payments with an excess over their bills that no supplier credit holds.
    </p>
  </div>
);

/** Rows per page. Named so the request and the pager cannot drift apart. */
const VOUCHER_PAGE_SIZE = 25;

export const AccountsPayableVouchersPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<PaymentVoucher[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [summary, setSummary] = useState<PaymentVoucherList['summary']>({
    paymentCount: 0,
    totalAmount: 0,
    allocatedAmount: 0,
    unallocatedAmount: 0,
  });
  const [search, setSearch] = useState('');
  // D49 phase 5: only payments whose excess no credit holds, so a leftover recorded
  // for an unmatched supplier can be found and dealt with.
  const [unallocatedOnly, setUnallocatedOnly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listPaymentVouchers({
        page,
        limit: VOUCHER_PAGE_SIZE,
        q: search.trim(),
        unallocatedOnly,
      });
      const data = res.data;
      setItems(data.items || []);
      setPages(Math.max(1, Number(data.pages || 1)));
      setSummary(
        data.summary || {
          paymentCount: 0,
          totalAmount: 0,
          allocatedAmount: 0,
          unallocatedAmount: 0,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = window.setTimeout(() => {
      void load();
    }, 200);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, unallocatedOnly]);

  const exportRows = useMemo(
    () =>
      items.map((row) => ({
        Reference: row.reference || '',
        Supplier: row.supplierName || '',
        'Cheque #': row.chequeNumber || '',
        'Online Ref': row.onlineReferenceNumber || '',
        'Payment Date': formatDate(row.paymentDate),
        Amount: row.amount ?? 0,
        Currency: row.currency || 'TTD',
        Method: row.method || '',
        Status: row.status === 'void' ? 'Void' : 'Applied',
        Unallocated: row.unallocatedAmount ?? 0,
        'Voided Reason': row.status === 'void' ? row.voidReason || '' : '',
        Note: row.note || '',
      })),
    [items]
  );

  const downloadCsv = () => {
    downloadObjectsCsv('payment-vouchers.csv', exportRows);
  };

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      <div className="px-8 pt-4 pb-3 bg-brand-bg border-b border-slate-200">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/accounts')}
              className="inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900"
            >
              <ArrowLeft aria-hidden="true" className="h-4 w-4" />
              Back
            </button>
            <h1 className="text-xl font-semibold leading-7 text-slate-900">Payment vouchers</h1>
            <ModuleInfoButton content={INFO} ariaLabel="Payment voucher information" />
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                placeholder="Search vouchers"
                aria-label="Search vouchers"
                className="h-10 w-64 rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-cyan-300"
              />
            </div>
            <label className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={unallocatedOnly}
                onChange={(e) => {
                  setPage(1);
                  setUnallocatedOnly(e.target.checked);
                }}
              />
              Unallocated only
            </label>
            <button
              onClick={() => load()}
              className="h-10 px-4 rounded-md border border-slate-300 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
            >
              Refresh
            </button>
            <button
              onClick={downloadCsv}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600"
            >
              Export CSV
            </button>
          </div>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          All payment vouchers created from Accounts Payable vouchers are tracked here.
        </p>
      </div>

      <div className="px-8 py-4 flex-1 overflow-auto">
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
          <SummaryCard label="Vouchers" value={summary.paymentCount} />
          {/* One line per currency, from the server's per-currency totals: the unlabelled top-level
              sums add a TT$ cheque to a US$ wire (lane 5, V3d follow-through). */}
          <SummaryCard label="Total Amount" value={<CurrencyLines byCurrency={summary.byCurrency} field="totalAmount" />} />
          <SummaryCard label="Allocated" value={<CurrencyLines byCurrency={summary.byCurrency} field="allocatedAmount" />} />
          <SummaryCard label="Unallocated" value={<CurrencyLines byCurrency={summary.byCurrency} field="unallocatedAmount" />} />
        </div>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <div className="overflow-auto">
            <table className="data-table min-w-[1300px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs text-slate-600">
                  <th className="px-4 py-3">Voucher Ref</th>
                  <th className="px-4 py-3">Supplier</th>
                  <th className="px-4 py-3">Cheque #</th>
                  <th className="px-4 py-3">Online Ref</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={9} className="px-4 py-10">
                      <LoadingState text="Loading payment vouchers..." />
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-10 text-center text-slate-500">
                      {unallocatedOnly ? 'No payments with an unallocated excess.' : 'No payment vouchers found.'}
                    </td>
                  </tr>
                )}
                {items.map((row) => (
                  <tr
                    key={row._id}
                    className={`border-b border-slate-100 hover:bg-slate-50 ${row.status === 'void' ? 'text-slate-400' : ''}`}
                  >
                    <td className="px-4 py-3 font-medium text-cyan-700 whitespace-nowrap">
                      <button onClick={() => navigate(`/accounts/payments/${row._id}`)} className="hover:underline">
                        {row.reference || '--'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-slate-700 min-w-[220px]">{row.supplierName || '--'}</td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{row.chequeNumber || '--'}</td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                      {row.onlineReferenceNumber || '--'}
                    </td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{formatDate(row.paymentDate)}</td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                      <span className={row.status === 'void' ? 'line-through' : ''}>
                        {formatMoney(row.amount, row.currency || 'TTD')}
                      </span>
                      {row.status !== 'void' && (row.unallocatedAmount || 0) > 0.01 ? (
                        <div className="text-xs text-amber-700">
                          Unallocated {formatMoney(row.unallocatedAmount, row.currency || 'TTD')}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{row.method || '--'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {row.status === 'void' ? (
                        <span
                          className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-800"
                          title={row.voidReason ? `Voided: ${row.voidReason}` : 'Voided'}
                        >
                          Void
                        </span>
                      ) : (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                          Applied
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/accounts/payments/${row._id}`)}
                        className="px-3 py-1.5 rounded-md border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination page={page} pageSize={VOUCHER_PAGE_SIZE} total={summary.paymentCount} onPageChange={setPage} noun="vouchers" />
        </div>
      </div>
    </div>
  );
};

function SummaryCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}
